import { StoreFilter } from "@CommonApi/merchflow/parse-variant-restrictions";
import { ResponseGetMerchflowStoreFacets } from "@CommonApi/merchflow/store-facets";
import { Button, Color, Flex, Icon, Input, MultiSelect, Text } from "src/elements";
import {
  countScopedStores,
  DraftGroup,
  DraftRule,
  findUnmatchedStoreCodes,
  isEmptyStoreFilter,
} from "../types";
import { RuleRow } from "./RuleRow";
import { useTranslation } from "src/i18n";

interface Props {
  group: DraftGroup;
  index: number;
  canRemove: boolean;
  facets: ResponseGetMerchflowStoreFacets | null;
  /** Rules involved in a conflict, highlighted so they stand out in the list. */
  conflictedRuleIds: ReadonlySet<string>;
  makeRuleId: () => string;
  onChange: (group: DraftGroup) => void;
  onRemove: (id: string) => void;
}

export const GroupCard = ({
  group,
  index,
  canRemove,
  facets,
  conflictedRuleIds,
  makeRuleId,
  onChange,
  onRemove,
}: Props) => {
  const { t } = useTranslation("planogramDelivery");
  const { id, storeInput, filter, unmatchedStores, rules } = group;

  const updateRule = (updated: DraftRule) => {
    onChange({ ...group, rules: rules.map((rule) => (rule.id === updated.id ? updated : rule)) });
  };

  const removeRule = (ruleId: string) => {
    onChange({ ...group, rules: rules.filter((rule) => rule.id !== ruleId) });
  };

  const addRule = () => {
    onChange({
      ...group,
      rules: [
        ...rules,
        {
          id: makeRuleId(),
          variant: "",
          matched: true,
          bayNumberRule: "",
          shelfRule: "",
          condition: "MUST_NOT_HAVE",
        },
      ],
    });
  };

  const setFilter = (patch: Partial<StoreFilter>) =>
    onChange({ ...group, filter: { ...filter, ...patch } });

  const numberPicker = (
    title: string,
    available: number[],
    selected: number[],
    key: "bay_sizes" | "section_heights" | "section_widths",
  ) =>
    available.length > 0 && (
      <MultiSelect
        key={key}
        title={title}
        allLabel={t("variantRestrictionDescribe.any")}
        width="150px"
        options={available.map((value) => ({ label: String(value), value: String(value) }))}
        selected={selected.map(String)}
        onChange={(values) => setFilter({ [key]: values.map(Number) })}
      />
    );

  const stringPicker = (
    title: string,
    available: string[],
    selected: string[],
    key: "states" | "clusters",
  ) =>
    available.length > 0 && (
      <MultiSelect
        key={key}
        title={title}
        allLabel={t("variantRestrictionDescribe.any")}
        width="150px"
        options={available.map((value) => ({ label: value, value }))}
        selected={selected}
        onChange={(values) => setFilter({ [key]: values })}
      />
    );

  const hasFacets =
    facets !== null &&
    (facets.bay_sizes.length > 0 ||
      facets.states.length > 0 ||
      facets.clusters.length > 0 ||
      facets.section_heights.length > 0 ||
      facets.section_widths.length > 0);

  const scoped = facets ? countScopedStores(storeInput, filter, facets.stores) : null;
  const filterSummary = [
    filter.bay_sizes.length &&
      `${t("variantRestrictionDescribe.baySize")}: ${filter.bay_sizes.join(", ")}`,
    filter.states.length && `${t("variantRestrictionDescribe.state")}: ${filter.states.join(", ")}`,
    filter.clusters.length &&
      `${t("variantRestrictionDescribe.cluster")}: ${filter.clusters.join(", ")}`,
    filter.section_heights.length &&
      `${t("variantRestrictionDescribe.sectionHeight")}: ${filter.section_heights.join(", ")}`,
    filter.section_widths.length &&
      `${t("variantRestrictionDescribe.sectionWidth")}: ${filter.section_widths.join(", ")}`,
  ].filter((line): line is string => Boolean(line));

  return (
    <Flex column gap="8px" padding="10px" borderColor={Color.lightGrayHover} borderRadius="4px">
      <Flex justify="between" align="center" gap="10px">
        <Text variant="small1" color={Color.spaceGray}>
          {t("variantRestrictionDescribe.groupName", { index: index + 1 })}
        </Text>

        {canRemove && (
          <Button
            name="remove-group"
            ariaLabel={t("variantRestrictionDescribe.removeStoreGroupAriaLabel")}
            variant="borderless"
            size="small"
            iconRight={{ name: "delete" }}
            onClick={() => onRemove(id)}
          />
        )}
      </Flex>

      <Input
        title={t("variantRestrictionDescribe.appliesToStores")}
        value={storeInput}
        isDisabled={facets === null}
        // Editing the field means the user is taking over the scope. Re-validate
        // the typed codes against the category's real stores (once facets are
        // loaded) rather than blindly clearing the warnings, so unknown codes
        // stay flagged and keep saving blocked.
        setValue={(next) =>
          onChange({
            ...group,
            storeInput: next,
            unmatchedStores: facets
              ? findUnmatchedStoreCodes(next, facets.stores)
              : unmatchedStores,
          })
        }
        placeholder={t("variantRestrictionDescribe.storeCodesPlaceholder")}
      />

      {/* Facets unavailable (still loading, or the request failed): the attribute
          pickers can't render, so surface the parsed filter as read-only text
          instead of hiding the scope entirely. */}
      {facets === null && !isEmptyStoreFilter(filter) && (
        <Flex column gap="2px">
          <Text variant="caption2" color={Color.textSecondary}>
            {t("variantRestrictionDescribe.parsedFilterUnavailable")}
          </Text>
          {filterSummary.map((line) => (
            <Text key={line} variant="caption2" color={Color.spaceGray}>
              {line}
            </Text>
          ))}
        </Flex>
      )}

      {facets !== null && !hasFacets && (
        <Text variant="caption2" color={Color.textSecondary}>
          {t("variantRestrictionDescribe.noStoreFilters")}
        </Text>
      )}

      {hasFacets && (
        // Generous vertical gaps: each picker carries a floating label (absolute,
        // top: -10px) that otherwise rides up into the caption above — or into the
        // row above once the pickers wrap. ~20px clears the label overflow.
        <Flex column gap="20px">
          <Text variant="caption2" color={Color.textSecondary}>
            {t("variantRestrictionDescribe.filterStoresByAttribute")}
          </Text>
          <Flex gap="20px" flexWrap="wrap">
            {numberPicker(
              t("variantRestrictionDescribe.baySize"),
              facets.bay_sizes,
              filter.bay_sizes,
              "bay_sizes",
            )}
            {stringPicker(
              t("variantRestrictionDescribe.state"),
              facets.states,
              filter.states,
              "states",
            )}
            {stringPicker(
              t("variantRestrictionDescribe.cluster"),
              facets.clusters,
              filter.clusters,
              "clusters",
            )}
            {numberPicker(
              t("variantRestrictionDescribe.sectionHeight"),
              facets.section_heights,
              filter.section_heights,
              "section_heights",
            )}
            {numberPicker(
              t("variantRestrictionDescribe.sectionWidth"),
              facets.section_widths,
              filter.section_widths,
              "section_widths",
            )}
          </Flex>
        </Flex>
      )}

      {scoped && (
        <Text variant="caption2" color={Color.primary}>
          {t(
            scoped.all
              ? scoped.count === 1
                ? "variantRestrictionDescribe.appliesToAllStore"
                : "variantRestrictionDescribe.appliesToAllStores"
              : scoped.count === 1
                ? "variantRestrictionDescribe.appliesToStore"
                : "variantRestrictionDescribe.appliesToStoresCount",
            { count: scoped.count },
          )}
        </Text>
      )}

      {unmatchedStores.length > 0 && (
        <Flex gap="6px" align="center">
          <Icon name="alert" color={Color.yellow} size={13} />
          <Text variant="caption2" color={Color.yellow}>
            {t(
              unmatchedStores.length === 1
                ? "variantRestrictionDescribe.unrecognisedStore"
                : "variantRestrictionDescribe.unrecognisedStores",
              { stores: unmatchedStores.join(", ") },
            )}
          </Text>
        </Flex>
      )}

      <Flex column borderColor={Color.lightGrayHover} borderRadius="4px">
        {rules.map((rule) => (
          <RuleRow
            key={rule.id}
            rule={rule}
            isConflicted={conflictedRuleIds.has(rule.id)}
            onChange={updateRule}
            onRemove={removeRule}
          />
        ))}
      </Flex>

      <Flex>
        <Button
          name="add-rule"
          variant="inverted"
          size="small"
          iconLeft={{ name: "plus" }}
          onClick={addRule}
        >
          {t("variantRestrictionDescribe.addRule")}
        </Button>
      </Flex>
    </Flex>
  );
};
